/**
 * 워크스페이스 (Organization) 선택 상태 — 사이드바 스위처가 변경하고
 * 각 페이지가 읽어서 그 조직 데이터만 필터링한다.
 *
 * 영속화: localStorage 에 마지막 선택 저장. 다음 접속 시 자동 복귀.
 *
 * 자동 선택 규칙:
 *   - 저장된 선택이 본인 organizations 목록에 있으면 그대로
 *   - 없으면 (조직 탈퇴 / 첫 접속 등) 목록의 첫 번째 (보통 Personal) 자동 선택
 *
 * 이 store 의 selectedOrgId 는 다음 페이지에서 필터링에 사용된다:
 *   - projects-page (현재 워크스페이스의 프로젝트만)
 *   - usage-page (사용량은 워크스페이스 단위)
 *   - dashboard-page
 *   - billing-page
 *
 * organizations-page (워크스페이스 관리) 와 admin/* 는 워크스페이스 무관 —
 * 모든 조직 / 모든 유저를 보여줘야 한다.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { OrganizationWithRole } from "@/lib/console-api";

interface WorkspaceState {
  selectedOrgId: string | null;
  setSelectedOrgId: (id: string | null) => void;
  /**
   * 사용자의 organizations 목록이 갱신될 때마다 호출 — 저장된 선택이 유효한지
   * 검증하고, 무효하면 첫 번째 조직으로 자동 fallback.
   */
  reconcile: (orgs: OrganizationWithRole[]) => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      selectedOrgId: null,
      setSelectedOrgId: (id) => set({ selectedOrgId: id }),
      reconcile: (orgs) => {
        const { selectedOrgId } = get();
        if (orgs.length === 0) {
          if (selectedOrgId !== null) set({ selectedOrgId: null });
          return;
        }
        const stillValid =
          selectedOrgId && orgs.some((o) => o.id === selectedOrgId);
        if (!stillValid) {
          // 첫 번째 조직 — Personal 이 보통 가입 시 자동 생성되어 첫 번째.
          set({ selectedOrgId: orgs[0]?.id ?? null });
        }
      },
    }),
    {
      name: "devit-web-workspace",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ selectedOrgId: s.selectedOrgId }),
    },
  ),
);
